import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Pagination,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";

import {
  getPaymentDueDemoList,
  insertPaymentDueDemo,
  updatePaymentDueDemo,
  deletePaymentDueDemo,
} from "../../../api/disticaret/vesselApi";

const initialFormState = {
  company: "",
  country: "",
  order: "",
  amount: "",
  currency: "USD",
  paymentDate: "",
  dueDate: "",
  explanation: "",
  reminderEnabled: true,
  reminderStartDaysBeforeDue: 7,
  reminderRepeatEveryDays: 2,
  reminderMaxCount: 10,
  reminderTimeOfDay: "09:00",
  nextReminderAt: "",
  ownerEmail: "",
};

const emptyFilters = {
  q: "",
  company: "",
  country: "",
  currency: "",
  type: "",
};

const PAGE_LIMIT = 25;

const defaultVisibleTypes = {
  PAID: true,
  OVERDUE: true,
  PENDING: true,
  "DUE TODAY": true,
};

function PaymentDueListDemo() {
  const { permissions } = useAuth();

  const [authToken, setAuthToken] = useState(() =>
    sessionStorage.getItem("token")
  );

  const hasFetchedInitialData = useRef(false);

  const canPost = permissions?.includes("vessel_post");
  const canUpdate = permissions?.includes("vessel_update");
  const canDelete = permissions?.includes("vessel_delete");

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [visibleTypes, setVisibleTypes] = useState(defaultVisibleTypes);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState(initialFormState);

  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [financialSummary, setFinancialSummary] = useState([]);

  const fmtNum = (num) => {
    const value = Number(num || 0);

    return value.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateTR = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return dateStr;

    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getFormDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const calculateClientType = (paymentDate, dueDate) => {
    if (paymentDate) return "PAID";
    if (!dueDate) return "PENDING";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    if (due.getTime() === today.getTime()) return "DUE TODAY";
    if (due < today) return "OVERDUE";

    return "PENDING";
  };

  const getTypeMeta = (type) => {
    switch (type) {
      case "PAID":
        return {
          bg: "success",
          text: "light",
          rowClass: "table-success",
          label: "PAID",
        };

      case "OVERDUE":
        return {
          bg: "danger",
          text: "light",
          rowClass: "table-danger",
          label: "OVERDUE",
        };

      case "DUE TODAY":
        return {
          bg: "warning",
          text: "dark",
          rowClass: "table-warning",
          label: "DUE TODAY",
        };

      case "PENDING":
      default:
        return {
          bg: "secondary",
          text: "light",
          rowClass: "",
          label: "PENDING",
        };
    }
  };

  const renderBadge = (type) => {
    const meta = getTypeMeta(type);

    return (
      <Badge
        bg={meta.bg}
        text={meta.text}
        style={{
          fontSize: "11px",
          padding: "5px 8px",
        }}
      >
        {meta.label}
      </Badge>
    );
  };

  const buildActiveFilters = useCallback(
    (filterState = {}, typeState = defaultVisibleTypes) => {
      const activeFilters = {};

      Object.entries(filterState).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          activeFilters[key] = String(value).trim();
        }
      });

      const selectedTypes = Object.entries(typeState)
        .filter(([, isVisible]) => isVisible === true)
        .map(([type]) => type);

      const allTypes = Object.keys(defaultVisibleTypes);

      if (
        !activeFilters.type &&
        selectedTypes.length > 0 &&
        selectedTypes.length < allTypes.length
      ) {
        activeFilters.type = selectedTypes;
      }

      return activeFilters;
    },
    []
  );

  const fetchData = useCallback(
    async (
      pageNumber = 1,
      filterState = {},
      typeState = defaultVisibleTypes
    ) => {
      setIsLoading(true);

      try {
        const activeFilters = buildActiveFilters(filterState, typeState);

        const params = {
          page: Number(pageNumber) || 1,
          limit: PAGE_LIMIT,
          ...activeFilters,
        };

        const res = await getPaymentDueDemoList(params);
        const payload = res?.data ? res.data : res;

        if (payload?.success === false) {
          alert(payload.message || "Liste alınırken hata oluştu.");
          return;
        }

        const responseData = payload?.data || {};

        const items =
          responseData.items ||
          responseData.docs ||
          responseData.rows ||
          responseData.list ||
          [];

        const total = Number(
          responseData.total ??
            responseData.totalRecords ??
            responseData.totalCount ??
            responseData.count ??
            0
        );

        const pages = Number(
          responseData.totalPages ??
            responseData.pageCount ??
            responseData.pages ??
            Math.max(1, Math.ceil(total / PAGE_LIMIT))
        );

        const safeTotalPages = Number.isFinite(pages) && pages > 0 ? pages : 1;

        const apiCurrentPage = Number(
          responseData.currentPage ?? responseData.page ?? pageNumber
        );

        const summary =
          responseData.financialSummary ||
          responseData.analytics ||
          responseData.summary ||
          [];

        setRows(Array.isArray(items) ? items : []);
        setFinancialSummary(Array.isArray(summary) ? summary : []);
        setTotalRecords(Number.isFinite(total) ? total : 0);
        setTotalPages(safeTotalPages);
        setCurrentPage(
          Number.isFinite(apiCurrentPage) && apiCurrentPage > 0
            ? apiCurrentPage
            : pageNumber
        );
      } catch (error) {
        console.error("Liste hatası:", error);

        if (error.response?.status === 401) {
          alert("Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapınız.");
        } else if (error.response?.status === 403) {
          alert("Bu listeyi görmeye yetkiniz yok.");
        } else {
          alert(
            error.response?.data?.message || "Liste alınırken hata oluştu."
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildActiveFilters]
  );

  useEffect(() => {
    if (authToken) return;

    const intervalId = setInterval(() => {
      const token = sessionStorage.getItem("token");

      if (token) {
        setAuthToken(token);
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    if (hasFetchedInitialData.current) return;

    hasFetchedInitialData.current = true;

    fetchData(1, emptyFilters);
  }, [authToken, fetchData]);

  const handlePageChange = (pageNumber) => {
    const nextPage = Number(pageNumber);

    if (!Number.isFinite(nextPage)) return;
    if (nextPage < 1) return;
    if (nextPage > totalPages) return;
    if (nextPage === currentPage) return;

    fetchData(nextPage, appliedFilters, visibleTypes);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const nextFilters = {
      ...filters,
    };

    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    fetchData(1, nextFilters, visibleTypes);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setVisibleTypes(defaultVisibleTypes);
    setCurrentPage(1);
    fetchData(1, emptyFilters, defaultVisibleTypes);
  };

  const toggleTypeFilter = (typeKey) => {
    const nextVisibleTypes = {
      ...visibleTypes,
      [typeKey]: !visibleTypes[typeKey],
    };

    setVisibleTypes(nextVisibleTypes);
    setCurrentPage(1);
    fetchData(1, appliedFilters, nextVisibleTypes);
  };

  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({
      key,
      direction,
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const buildSavePayload = () => {
    return {
      company: form.company || "",
      country: form.country || "",
      order: form.order || "",
      amount: form.amount === "" ? 0 : Number(form.amount),
      currency: form.currency || "",
      paymentDate: form.paymentDate || null,
      dueDate: form.dueDate || null,
      explanation: form.explanation || "",
      reminderEnabled: Boolean(form.reminderEnabled),
      reminderStartDaysBeforeDue: Number(form.reminderStartDaysBeforeDue || 0),
      reminderRepeatEveryDays: Number(form.reminderRepeatEveryDays || 1),
      reminderMaxCount: Number(form.reminderMaxCount || 0),
      reminderTimeOfDay: form.reminderTimeOfDay || "09:00",
      nextReminderAt: form.nextReminderAt || null,
      ownerEmail: form.ownerEmail || "",
    };
  };

  const handleSave = async () => {
    if (!form.company || !form.order) {
      return alert("Lütfen şirket ve order bilgisi giriniz.");
    }

    setIsLoading(true);

    try {
      const payload = buildSavePayload();

      if (editRow) {
        payload.id = editRow._id;
        await updatePaymentDueDemo(payload);
        alert("Kayıt başarıyla güncellendi.");
      } else {
        await insertPaymentDueDemo(payload);
        alert("Yeni kayıt başarıyla eklendi.");
      }

      await fetchData(currentPage, appliedFilters, visibleTypes);
      setShowModal(false);
      setEditRow(null);
      setForm(initialFormState);
    } catch (error) {
      console.error("Kayıt hatası:", error);
      alert(
        error.response?.data?.message || "İşlem sırasında bir hata oluştu."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow) return;

    setIsLoading(true);

    try {
      await deletePaymentDueDemo({
        id: deleteRow._id,
      });

      alert("Kayıt başarıyla silindi.");
      setDeleteRow(null);
      await fetchData(currentPage, appliedFilters, visibleTypes);
    } catch (error) {
      console.error("Silme hatası:", error);
      alert(error.response?.data?.message || "Silme işlemi başarısız.");
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setForm(initialFormState);
    setEditRow(null);
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditRow(row);

    setForm({
      ...initialFormState,
      ...row,
      amount: row.amount ?? "",
      paymentDate: getFormDate(row.paymentDate),
      dueDate: getFormDate(row.dueDate),
      nextReminderAt: getFormDate(row.nextReminderAt),
    });

    setShowModal(true);
  };

  const tableColSpan = 8 + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);

  const sortedAndFilteredRows = useMemo(() => {
    let filteredItems = Array.isArray(rows) ? [...rows] : [];

    if (sortConfig.key !== null) {
      filteredItems = filteredItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "company") {
          aValue = (a.company || "").toLowerCase();
          bValue = (b.company || "").toLowerCase();
        }

        if (sortConfig.key === "order") {
          aValue = (a.order || "").toLowerCase();
          bValue = (b.order || "").toLowerCase();
        }

        if (sortConfig.key === "currency") {
          aValue = (a.currency || "").toLowerCase();
          bValue = (b.currency || "").toLowerCase();
        }

        if (sortConfig.key === "amount") {
          aValue = Number(a.amount || 0);
          bValue = Number(b.amount || 0);
        }

        if (sortConfig.key === "paymentDate" || sortConfig.key === "dueDate") {
          aValue = new Date(aValue || "1970-01-01").getTime();
          bValue = new Date(bValue || "1970-01-01").getTime();
        }

        if (sortConfig.key === "type") {
          aValue = a.type || calculateClientType(a.paymentDate, a.dueDate);
          bValue = b.type || calculateClientType(b.paymentDate, b.dueDate);
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

        return 0;
      });
    }

    return filteredItems;
  }, [rows, sortConfig]);

  const summaryCards = useMemo(() => {
    return (Array.isArray(financialSummary) ? financialSummary : []).map(
      (item) => {
        const currency = String(item.currency || "N/A").toUpperCase();

        return {
          currency,
          total: Number(item.total || 0),
          paid: Number(item.paid || item.PAID || 0),
          overdue: Number(item.overdue || item.OVERDUE || 0),
          pending: Number(item.pending || item.PENDING || 0),
          dueToday: Number(
            item.dueToday || item.due_today || item["DUE TODAY"] || 0
          ),
        };
      }
    );
  }, [financialSummary]);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Ödeme Takip</h4>
          <small className="text-muted">
            Toplam {totalRecords} kayıt bulundu.
          </small>
        </div>

        {canPost && (
          <Button
            variant="success"
            size="sm"
            style={{
              whiteSpace: "nowrap",
              height: "fit-content",
            }}
            onClick={openCreateModal}
          >
            Yeni Ekle
          </Button>
        )}
      </div>

      <div className="bg-white p-3 rounded shadow-sm border mb-4">
        <Row className="gy-3 align-items-end">
          <Col md={8}>
            <Form onSubmit={handleSearch} className="d-flex gap-2 flex-wrap">
              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Genel Arama
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Ara..."
                  value={filters.q}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      q: e.target.value,
                    })
                  }
                  style={{ width: "150px" }}
                />
              </div>

              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Şirket
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Şirket..."
                  value={filters.company}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      company: e.target.value,
                    })
                  }
                  style={{ width: "150px" }}
                />
              </div>

              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Ülke
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Ülke..."
                  value={filters.country}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      country: e.target.value,
                    })
                  }
                  style={{ width: "130px" }}
                />
              </div>

              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Para Birimi
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.currency}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      currency: e.target.value,
                    })
                  }
                  style={{ width: "130px" }}
                >
                  <option value="">Tümü</option>
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                  <option value="SAR">SAR</option>
                </Form.Select>
              </div>

              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.type}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      type: e.target.value,
                    })
                  }
                  style={{ width: "140px" }}
                >
                  <option value="">Tümü</option>
                  <option value="PAID">PAID</option>
                  <option value="OVERDUE">OVERDUE</option>
                  <option value="PENDING">PENDING</option>
                  <option value="DUE TODAY">DUE TODAY</option>
                </Form.Select>
              </div>

              <div className="d-flex align-items-end pb-1 gap-2">
                <Button size="sm" type="submit" variant="primary">
                  Ara
                </Button>

                <Button
                  size="sm"
                  type="button"
                  variant="outline-secondary"
                  onClick={handleClearFilters}
                >
                  Temizle
                </Button>
              </div>
            </Form>
          </Col>

          <Col md={4}>
            <div className="bg-light p-2 rounded border h-100">
              <span className="d-block text-muted small fw-bold border-bottom pb-1 mb-2">
                Gösterilecek Tipler
              </span>

              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  type="switch"
                  id="type-paid"
                  label="Paid"
                  checked={visibleTypes.PAID}
                  onChange={() => toggleTypeFilter("PAID")}
                  className="text-success fw-bold small"
                />

                <Form.Check
                  type="switch"
                  id="type-overdue"
                  label="Overdue"
                  checked={visibleTypes.OVERDUE}
                  onChange={() => toggleTypeFilter("OVERDUE")}
                  className="text-danger fw-bold small"
                />

                <Form.Check
                  type="switch"
                  id="type-pending"
                  label="Pending"
                  checked={visibleTypes.PENDING}
                  onChange={() => toggleTypeFilter("PENDING")}
                  className="text-secondary fw-bold small"
                />

                <Form.Check
                  type="switch"
                  id="type-due-today"
                  label="Due Today"
                  checked={visibleTypes["DUE TODAY"]}
                  onChange={() => toggleTypeFilter("DUE TODAY")}
                  className="text-warning fw-bold small"
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <div className="table-responsive">
          {isLoading && !showModal && !deleteRow ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Yükleniyor...</p>
            </div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{
                fontSize: "13px",
                whiteSpace: "nowrap",
              }}
            >
              <thead className="table-light">
                <tr style={{ height: "40px" }}>
                  <th
                    onClick={() => requestSort("company")}
                    style={{ cursor: "pointer" }}
                  >
                    COMPANY / COUNTRY {getSortIcon("company")}
                  </th>

                  <th
                    onClick={() => requestSort("order")}
                    style={{ cursor: "pointer" }}
                  >
                    ORDER {getSortIcon("order")}
                  </th>

                  <th
                    className="text-end"
                    onClick={() => requestSort("amount")}
                    style={{ cursor: "pointer" }}
                  >
                    AMOUNT {getSortIcon("amount")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("currency")}
                    style={{ cursor: "pointer" }}
                  >
                    CURRENCY {getSortIcon("currency")}
                  </th>

                  <th
                    onClick={() => requestSort("paymentDate")}
                    style={{ cursor: "pointer" }}
                  >
                    PAYMENT DATE {getSortIcon("paymentDate")}
                  </th>

                  <th
                    onClick={() => requestSort("dueDate")}
                    style={{ cursor: "pointer" }}
                  >
                    DUE DATE {getSortIcon("dueDate")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("type")}
                    style={{ cursor: "pointer" }}
                  >
                    TYPE {getSortIcon("type")}
                  </th>

                  <th>EXPLANATION</th>

                  {canUpdate && <th className="text-center">DÜZENLE</th>}
                  {canDelete && <th className="text-center">SİL</th>}
                </tr>
              </thead>

              <tbody>
                {sortedAndFilteredRows.length > 0 ? (
                  sortedAndFilteredRows.map((r) => {
                    const rowType =
                      r.type || calculateClientType(r.paymentDate, r.dueDate);

                    const typeMeta = getTypeMeta(rowType);

                    return (
                      <Fragment key={r._id}>
                        <tr className={typeMeta.rowClass}>
                          <td className="px-3">
                            <div className="fw-bold text-dark">
                              {r.company || "-"}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {r.country || "-"}
                            </div>
                          </td>

                          <td className="px-3 fw-bold">{r.order || "-"}</td>

                          <td className="text-end px-3">
                            <span
                              className="fw-bold text-primary"
                              style={{ fontSize: "14px" }}
                            >
                              {fmtNum(r.amount)}
                            </span>
                          </td>

                          <td className="text-center px-3">
                            <Badge bg="light" text="dark">
                              {r.currency || "-"}
                            </Badge>
                          </td>

                          <td className="px-3">
                            {formatDateTR(r.paymentDate)}
                          </td>

                          <td className="px-3">{formatDateTR(r.dueDate)}</td>

                          <td className="text-center px-3">
                            {renderBadge(rowType)}
                          </td>

                          <td
                            className="px-3 text-truncate"
                            style={{ maxWidth: "220px" }}
                            title={r.explanation}
                          >
                            {r.explanation || ""}
                          </td>

                          {canUpdate && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-decoration-none"
                                style={{ fontSize: "12px" }}
                                onClick={() => openEditModal(r)}
                              >
                                DÜZENLE
                              </Button>
                            </td>
                          )}

                          {canDelete && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-danger text-decoration-none"
                                style={{ fontSize: "12px" }}
                                onClick={() => setDeleteRow(r)}
                              >
                                SİL
                              </Button>
                            </td>
                          )}
                        </tr>
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="text-center py-4 text-muted"
                    >
                      Kayıt bulunamadı veya mevcut filtrelere uygun veri yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && sortedAndFilteredRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border-top">
            <small className="text-muted">
              Toplam <b>{totalRecords}</b> kayıttan{" "}
              <b>
                {(currentPage - 1) * PAGE_LIMIT + 1} -{" "}
                {Math.min(currentPage * PAGE_LIMIT, totalRecords)}
              </b>{" "}
              arası.
            </small>

            <Pagination size="sm" className="m-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />

              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(Number(totalPages) || 1)].map((_, i) => {
                const pageNum = i + 1;

                if (
                  totalPages > 10 &&
                  Math.abs(currentPage - pageNum) > 3 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  return null;
                }

                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />

              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card>

      {!isLoading && summaryCards.length > 0 && (
        <div className="mt-4 mb-5">
          <h5
            className="fw-bold text-secondary mb-3"
            style={{ letterSpacing: "-0.5px" }}
          >
            Filtrelenmiş Kayıtların Finansal Özeti
          </h5>

          <Row className="g-3">
            {summaryCards.map((data) => (
              <Col lg={3} md={4} sm={6} key={data.currency}>
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{
                    borderRadius: "10px",
                    borderTop: "4px solid #1976d2",
                  }}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted fw-bold small">
                        {data.currency} TOPLAM
                      </span>

                      <Badge bg="primary" className="fs-6">
                        {fmtNum(data.total)}
                      </Badge>
                    </div>

                    <hr className="my-2" style={{ opacity: 0.1 }} />

                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Paid:</span>
                      <span className="fw-bold text-success">
                        {fmtNum(data.paid)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Overdue:</span>
                      <span className="fw-bold text-danger">
                        {fmtNum(data.overdue)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Pending:</span>
                      <span className="fw-bold text-secondary">
                        {fmtNum(data.pending)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Due Today:</span>
                      <span className="fw-bold text-warning">
                        {fmtNum(data.dueToday)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editRow ? "Kaydı Düzenle" : "Yeni Kayıt Ekle"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.company}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        company: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.country}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        country: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Order</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.order}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        order: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        amount: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    value={form.currency}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        currency: e.target.value,
                      })
                    }
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                    <option value="GBP">GBP</option>
                    <option value="AED">AED</option>
                    <option value="SAR">SAR</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Payment Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={getFormDate(form.paymentDate)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        paymentDate: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={getFormDate(form.dueDate)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        dueDate: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <div className="pt-2">
                    {renderBadge(
                      calculateClientType(form.paymentDate, form.dueDate)
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Type backend tarafında otomatik hesaplanır.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Explanation</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.explanation}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        explanation: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <h6 className="fw-bold mb-3">Reminder Ayarları</h6>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Check
                  type="switch"
                  id="reminderEnabled"
                  label="Reminder Aktif"
                  checked={form.reminderEnabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reminderEnabled: e.target.checked,
                    })
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Başlangıç Gün</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.reminderStartDaysBeforeDue}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        reminderStartDaysBeforeDue: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Tekrar Gün</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.reminderRepeatEveryDays}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        reminderRepeatEveryDays: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Max Count</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.reminderMaxCount}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        reminderMaxCount: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Reminder Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={form.reminderTimeOfDay}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        reminderTimeOfDay: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Next Reminder At</Form.Label>
                  <Form.Control
                    type="date"
                    value={getFormDate(form.nextReminderAt)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        nextReminderAt: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Owner Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        ownerEmail: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            İptal
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteRow !== null}
        onHide={() => setDeleteRow(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Kayıt Silme Onayı</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>
            <strong>{deleteRow?.order}</strong> numaralı siparişe ait kaydı
            silmek istediğinize emin misiniz?
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRow(null)}>
            Vazgeç
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default PaymentDueListDemo;
